import { useState, useEffect, useRef, useCallback } from 'react';
import { SKIP_INDICATOR_SESSION_TIMEOUT_MILLISECONDS } from '@/constants/video-player';

const SKIP_INDICATOR_SESSION_TIMEOUT_SEC =
    SKIP_INDICATOR_SESSION_TIMEOUT_MILLISECONDS / 1000;

export enum SkipDirection {
    Forward = 'forward',
    Backward = 'backward',
}

interface UseSkipIndicatorOptions {
    enabled?: boolean;
    duration?: number;
    currentTime?: number;
}

interface UseSkipIndicatorReturn {
    isVisible: boolean;
    direction: SkipDirection | null;
    accumulatedSeconds: number;
    handleSkip: (
        seconds: number,
        direction: SkipDirection,
        positionInRange?: number,
    ) => void;
    reset: () => void;
}

export function useSkipIndicator({
    enabled = true,
    duration,
    currentTime = 0,
}: UseSkipIndicatorOptions): UseSkipIndicatorReturn {
    const [isVisible, setIsVisible] = useState(false);
    const [direction, setDirection] = useState<SkipDirection | null>(null);
    const [accumulatedSeconds, setAccumulatedSeconds] = useState(0);
    const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastDirectionRef = useRef<SkipDirection | null>(null);

    const resetIndicator = useCallback(() => {
        setIsVisible(false);
        setDirection(null);
        setAccumulatedSeconds(0);
        lastDirectionRef.current = null;
        if (sessionTimeoutRef.current) {
            clearTimeout(sessionTimeoutRef.current);
            sessionTimeoutRef.current = null;
        }
    }, []);

    const handleSkip = useCallback(
        (
            seconds: number,
            skipDirection: SkipDirection,
            positionInRange?: number,
        ) => {
            if (!enabled) return;

            const position = positionInRange ?? currentTime;
            const rangeDuration = duration ?? 0;
            const hasRange = rangeDuration > 0;

            const isAtStart =
                hasRange && position <= SKIP_INDICATOR_SESSION_TIMEOUT_SEC;
            const isAtEnd =
                hasRange &&
                position >= rangeDuration - SKIP_INDICATOR_SESSION_TIMEOUT_SEC;
            const isBackwardSkip = skipDirection === SkipDirection.Backward;
            const isForwardSkip = skipDirection === SkipDirection.Forward;

            if ((isAtStart && isBackwardSkip) || (isAtEnd && isForwardSkip)) {
                resetIndicator();
                return;
            }

            const isDirectionChange =
                lastDirectionRef.current !== null &&
                lastDirectionRef.current !== skipDirection;
            const skipAmount = Math.abs(seconds);

            if (isDirectionChange) {
                const newValue =
                    rangeDuration <= 0
                        ? skipAmount
                        : Math.min(skipAmount, rangeDuration);
                setAccumulatedSeconds(newValue);
                setDirection(skipDirection);
                setIsVisible(true);
                lastDirectionRef.current = skipDirection;
            } else {
                setAccumulatedSeconds((prev) => {
                    const newValue = prev + skipAmount;
                    return rangeDuration > 0
                        ? Math.min(newValue, rangeDuration)
                        : newValue;
                });
                setDirection(skipDirection);
                setIsVisible(true);
                lastDirectionRef.current = skipDirection;
            }

            if (sessionTimeoutRef.current) {
                clearTimeout(sessionTimeoutRef.current);
            }

            sessionTimeoutRef.current = setTimeout(() => {
                resetIndicator();
            }, SKIP_INDICATOR_SESSION_TIMEOUT_MILLISECONDS);
        },
        [enabled, resetIndicator, duration, currentTime],
    );

    useEffect(() => {
        return () => {
            if (sessionTimeoutRef.current) {
                clearTimeout(sessionTimeoutRef.current);
            }
        };
    }, []);

    return {
        isVisible,
        direction,
        accumulatedSeconds,
        handleSkip,
        reset: resetIndicator,
    };
}
